import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import portalApi from "../../api/portalClient";
import { useBranding } from "../../context/BrandingContext";
import { Ticket, TicketComment } from "../../types";
import TicketAttachments from "../../components/TicketAttachments";
import { formatDateTime } from "../../utils/datetime";

const STATUS_LABEL: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  on_hold: "On Hold",
  resolved: "Resolved",
  closed: "Closed",
};

const STATUS_STYLES: Record<string, string> = {
  open: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800",
  in_progress:
    "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800",
  on_hold:
    "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700/40 dark:text-gray-300 dark:border-gray-600",
  resolved:
    "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800",
  closed:
    "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700/40 dark:text-gray-400 dark:border-gray-600",
};

const PRIORITY_DOT: Record<string, string> = {
  urgent: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-yellow-500",
  low: "bg-gray-400",
};

export default function PortalTicketDetail() {
  const branding = useBranding();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [t, c] = await Promise.all([
        portalApi.get(`/api/v1/portal/tickets/${id}`),
        portalApi.get(`/api/v1/portal/tickets/${id}/comments`),
      ]);
      setTicket(t.data);
      setComments(c.data);
    } catch (e: any) {
      setError("Ticket not found");
    }
  };
  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [id]);

  const submitReply = async () => {
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    try {
      await portalApi.post(`/api/v1/portal/tickets/${id}/comments`, { body });
      setReply("");
      await load();
    } finally {
      setSending(false);
    }
  };

  if (error)
    return (
      <div className="max-w-6xl">
        <p className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/40 dark:border-red-800 dark:text-red-300 text-sm px-4 py-3 rounded-lg">
          {error}
        </p>
      </div>
    );
  if (!ticket) return <div className="text-gray-400 text-sm">Loading…</div>;

  return (
    <div className="max-w-6xl">
      <button
        onClick={() => navigate("/portal/tickets")}
        className="text-sm text-brand dark:text-brand-light hover:underline mb-4 inline-flex items-center gap-1 font-medium"
      >
        <span aria-hidden>&larr;</span> My Tickets
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden mb-4">
        <div className="h-1 w-full bg-brand" />
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold mb-1">
                Ticket #{ticket.id}
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 break-words">
                {ticket.title}
              </h1>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Opened {formatDateTime(ticket.created_at)}
                {ticket.assigned_to_username && (
                  <>
                    {" "}
                    · Working with{" "}
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      {ticket.assigned_to_username}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <span
                className={`text-[11px] px-2.5 py-1 rounded-full font-semibold border ${
                  STATUS_STYLES[ticket.status] ||
                  "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700/40 dark:text-gray-300 dark:border-gray-600"
                }`}
              >
                {STATUS_LABEL[ticket.status] || ticket.status}
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-gray-50 text-gray-700 border border-gray-200 dark:bg-gray-900/50 dark:text-gray-300 dark:border-gray-600 capitalize">
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    PRIORITY_DOT[ticket.priority] || "bg-gray-400"
                  }`}
                />
                {ticket.priority}
              </span>
              {ticket.waiting_on_customer && (
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-800 font-semibold">
                  Waiting for your response
                </span>
              )}
            </div>
          </div>
          {ticket.description && (
            <p className="mt-5 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed border-t border-gray-100 dark:border-gray-700 pt-5">
              {ticket.description}
            </p>
          )}
        </div>
      </div>

      {/* Attachments. Uploading is hidden once the ticket is closed — a file
          sent to a closed ticket notifies nobody, and offering the box implies
          somebody is going to look at it. */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm mb-6">
        <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Attachments</h2>
        </div>
        <div className="p-5">
          <TicketAttachments
            client={portalApi}
            basePath={`/api/v1/portal/tickets/${ticket.id}`}
            canDelete={false}
            canUpload={ticket.status !== "closed"}
          />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Conversation</h2>
        </div>
        <div className="p-5">
          <div className="space-y-3 mb-4">
            {comments.length === 0 && (
              <p className="text-center text-gray-400 dark:text-gray-500 text-sm py-6">
                No replies yet.
              </p>
            )}
            {comments.map((c) => (
              <div
                key={c.id}
                className="border border-gray-100 dark:border-gray-700 rounded-lg p-4 bg-gray-50/60 dark:bg-gray-900/50 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
              >
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                  <strong className="text-gray-800 dark:text-gray-200">
                    {c.author_username || branding.company_name}
                  </strong>
                  <span className="mx-1.5">·</span>
                  {formatDateTime(c.created_at)}
                </div>
                <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                  {c.body}
                </div>
              </div>
            ))}
          </div>

          {ticket.status !== "closed" && (
            <div className="flex flex-col gap-2 border-t border-gray-100 dark:border-gray-700 pt-4">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                placeholder={
                  ticket.waiting_on_customer
                    ? `${branding.company_name} is waiting on your response — type here…`
                    : "Add a reply or new information…"
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
              />
              <div className="flex justify-end">
                <button
                  onClick={submitReply}
                  disabled={sending || !reply.trim()}
                  className="bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-5 py-2 rounded-lg shadow-sm disabled:opacity-50 transition-colors"
                >
                  {sending ? "Sending…" : "Send reply"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
